import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      const sections = ['home', 'about', 'services', 'hours', 'experience', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5aj5tfdcu97hjrn3lc0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="https://public.readdy.ai/ai/img_res/5dfa6018-d6ac-4521-84f9-81506e526463.png" 
                alt="Dr Amit Physiotherapy Clinic Logo" 
                className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
              />
              <div>
                <h2 className={`text-base sm:text-lg font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>Dr Amit Physiotherapy Clinic</h2>
                <p className={`text-xs transition-colors ${isScrolled ? 'text-gray-600' : 'text-white/90'}`}>Expert Care, Faster Recovery</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-8">
              {[
                { id: 'home', label: 'Home' },
                { id: 'about', label: 'About' },
                { id: 'services', label: 'Services' },
                { id: 'hours', label: 'Hours' },
                { id: 'experience', label: 'Experience' },
                { id: 'contact', label: 'Contact' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeSection === item.id
                      ? isScrolled ? 'text-teal-600' : 'text-white font-semibold'
                      : isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white/90 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => scrollToSection('contact')}
              className="hidden lg:block bg-teal-600 text-white px-4 xl:px-6 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Book Appointment
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                isScrolled ? 'text-gray-900 hover:bg-gray-100' : 'text-white hover:bg-white/10'
              }`}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileMenuOpen ? 'max-h-96 mt-4' : 'max-h-0'}`}>
            <div className={`flex flex-col gap-2 py-4 ${isScrolled ? 'bg-white' : 'bg-black/20 backdrop-blur-md rounded-xl'}`}>
              {[
                { id: 'home', label: 'Home' },
                { id: 'about', label: 'About' },
                { id: 'services', label: 'Services' },
                { id: 'hours', label: 'Hours' },
                { id: 'experience', label: 'Experience' },
                { id: 'contact', label: 'Contact' }
              ].map(item => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-left px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                    activeSection === item.id
                      ? isScrolled ? 'text-teal-600 bg-teal-50' : 'text-white bg-white/10'
                      : isScrolled ? 'text-gray-700 hover:bg-gray-50' : 'text-white/90 hover:bg-white/5'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => scrollToSection('contact')}
                className="mx-4 mt-2 bg-teal-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-center"
              >
                Book Appointment
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/6ff32357ddc22f104e2f2bd16ca02b81.jpg"
            alt="Physiotherapy Clinic"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center px-4 sm:px-6 w-full max-w-5xl mx-auto animate-fade-in-up">
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Welcome to<br />Dr Amit Physiotherapy Clinic
          </h1>
          <p className="text-base sm:text-xl md:text-2xl text-white/95 mb-6 sm:mb-8 max-w-3xl mx-auto leading-relaxed">
            Experience professional physiotherapy care with Dr. Amit Sharma - 20 years of expertise in helping patients recover, rehabilitate, and regain their quality of life
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => scrollToSection('contact')}
              className="w-full sm:w-auto bg-teal-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer shadow-xl"
            >
              Schedule Consultation
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              Our Services
            </button>
          </div>
          <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-3xl mx-auto">
            {[
              { number: '20+', label: 'Years Experience', delay: '0ms' },
              { number: '5000+', label: 'Patients Treated', delay: '100ms' },
              { number: '98%', label: 'Success Rate', delay: '200ms' }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="bg-white/10 backdrop-blur-md rounded-2xl p-4 sm:p-6 border border-white/20 animate-scale-in"
                style={{ animationDelay: stat.delay }}
              >
                <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.number}</div>
                <div className="text-white/90 text-xs sm:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Dr. Amit Sharma */}
      <section id="about" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-1 gap-12">
            <div className="animate-fade-in-up">
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Meet Your Physiotherapist
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Dr. Amit Sharma
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                With over 20 years of dedicated experience in physiotherapy, Dr. Amit Sharma has established himself as a trusted healthcare professional in Ghaziabad. His commitment to patient care and evidence-based treatment approaches has helped thousands of patients recover from various musculoskeletal conditions and injuries.
              </p>
              <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                Dr. Sharma specializes in manual therapy, sports rehabilitation, post-operative care, and chronic pain management. His patient-centered approach ensures that each individual receives personalized treatment plans tailored to their specific needs and recovery goals.
              </p>
              <div className="space-y-4">
                {[
                  { title: 'Certified Physiotherapist', desc: 'Licensed and registered with professional medical boards' },
                  { title: 'Advanced Training', desc: 'Specialized in modern physiotherapy techniques and rehabilitation' },
                  { title: 'Patient-Focused Care', desc: 'Dedicated to achieving the best outcomes for every patient' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 animate-slide-in-left" style={{ animationDelay: `${index * 100}ms` }}>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <strong className="text-gray-900">{item.title}</strong>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in-up">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Our Expertise
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Comprehensive Physiotherapy Services
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a wide range of specialized physiotherapy treatments designed to help you recover faster and live pain-free
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: 'ri-heart-pulse-line',
                title: 'Sports Injury Rehabilitation',
                description: 'Specialized treatment for athletes and sports-related injuries including sprains, strains, and post-injury recovery programs'
              },
              {
                icon: 'ri-wheelchair-line',
                title: 'Post-Operative Care',
                description: 'Comprehensive rehabilitation programs following orthopedic surgeries to restore mobility and strength'
              },
              {
                icon: 'ri-pulse-line',
                title: 'Chronic Pain Management',
                description: 'Evidence-based treatments for managing chronic back pain, neck pain, arthritis, and other persistent conditions'
              },
              {
                icon: 'ri-hand-heart-line',
                title: 'Manual Therapy',
                description: 'Hands-on techniques including joint mobilization, soft tissue massage, and myofascial release'
              },
              {
                icon: 'ri-run-line',
                title: 'Mobility & Strength Training',
                description: 'Customized exercise programs to improve flexibility, strength, balance, and overall physical function'
              },
              {
                icon: 'ri-user-heart-line',
                title: 'Geriatric Physiotherapy',
                description: 'Specialized care for elderly patients focusing on fall prevention, balance, and age-related conditions'
              }
            ].map((service, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 border border-gray-100 animate-scale-in cursor-pointer"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-xl mb-4 sm:mb-6">
                  <i className={`${service.icon} text-2xl sm:text-3xl text-teal-600`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operating Hours */}
      <section id="hours" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-1 gap-12">
            <div className="animate-fade-in-up">
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Visit Us
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
                Operating Hours
              </h2>
              <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed">
                We understand that your time is valuable. Our clinic operates during convenient morning and evening hours to accommodate your busy schedule.
              </p>
              
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white shadow-xl mb-6 sm:mb-8 animate-scale-in">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                    <i className="ri-time-line text-xl sm:text-2xl"></i>
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm font-medium text-teal-100">Daily Schedule</div>
                    <div className="text-lg sm:text-xl font-bold">Morning & Evening Sessions</div>
                  </div>
                </div>
                <div className="border-t border-white/20 pt-6">
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="text-xs sm:text-sm text-teal-100 mb-2">Morning Session</div>
                      <div className="text-xl sm:text-2xl font-bold">9:00 AM - 12:00 PM</div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="text-xs sm:text-sm text-teal-100 mb-2">Evening Session</div>
                      <div className="text-xl sm:text-2xl font-bold">5:00 PM - 9:00 PM</div>
                    </div>
                    <div className="border-t border-white/20 pt-4 mt-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-xs sm:text-sm text-teal-100 mb-1">Monday - Saturday</div>
                          <div className="font-semibold">Open</div>
                        </div>
                        <div>
                          <div className="text-xs sm:text-sm text-teal-100 mb-1">Sunday</div>
                          <div className="font-semibold">By Appointment</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 sm:p-6 rounded-lg">
                <div className="flex gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-amber-600 text-xl"></i>
                  </div>
                  <div>
                    <strong className="text-amber-900 block mb-1">Appointment Recommended</strong>
                    <p className="text-amber-800 text-sm">Please call ahead to schedule your visit and avoid waiting times</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section id="experience" className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in-up">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Proven Track Record
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Two Decades of Excellence
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Dr. Amit Sharma's 20 years of experience has transformed thousands of lives through dedicated physiotherapy care
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {[
              { number: '20+', label: 'Years in Practice', icon: 'ri-calendar-check-line' },
              { number: '5000+', label: 'Patients Treated', icon: 'ri-user-heart-line' },
              { number: '15+', label: 'Specializations', icon: 'ri-award-line' },
              { number: '98%', label: 'Success Rate', icon: 'ri-star-line' }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-6 sm:p-8 text-center shadow-lg border border-gray-100 animate-scale-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-xl mx-auto mb-4">
                  <i className={`${stat.icon} text-xl sm:text-2xl text-teal-600`}></i>
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-xs sm:text-sm text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden animate-fade-in-up">
            <div className="p-6 sm:p-12">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Areas of Expertise</h3>
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                {[
                  'Orthopedic Rehabilitation',
                  'Neurological Physiotherapy',
                  'Sports Medicine & Injury Prevention',
                  'Pediatric Physiotherapy',
                  'Women\'s Health Physiotherapy',
                  'Cardiopulmonary Rehabilitation',
                  'Workplace Ergonomics',
                  'Postural Correction'
                ].map((expertise, index) => (
                  <div key={index} className="flex items-center gap-3 animate-slide-in-left" style={{ animationDelay: `${index * 50}ms` }}>
                    <div className="w-6 h-6 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0">
                      <i className="ri-check-line text-teal-600 text-sm"></i>
                    </div>
                    <span className="text-sm sm:text-base text-gray-700 font-medium">{expertise}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16 animate-fade-in-up">
            <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Get In Touch
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              Contact Us
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              Schedule your appointment today and take the first step towards recovery and better health
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
            <div className="animate-slide-in-left">
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Clinic Information</h3>
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                      <i className="ri-map-pin-line text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Address</div>
                      <p className="text-teal-50 text-sm leading-relaxed">
                        A 62, Block A, Brij Vihar,<br />
                        Ghaziabad, Uttar Pradesh 201011
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                      <i className="ri-phone-line text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Phone</div>
                      <a href="tel:09999742835" className="text-teal-50 text-sm hover:text-white transition-colors cursor-pointer">
                        099997 42835
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                      <i className="ri-mail-line text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Email</div>
                      <a href="mailto:dramitsharma.62@gmail.com" className="text-teal-50 text-sm hover:text-white transition-colors cursor-pointer break-all">
                        dramitsharma.62@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                      <i className="ri-time-line text-xl sm:text-2xl"></i>
                    </div>
                    <div>
                      <div className="font-semibold mb-1">Operating Hours</div>
                      <p className="text-teal-50 text-sm">
                        Morning: 9:00 AM - 12:00 PM<br />
                        Evening: 5:00 PM - 9:00 PM<br />
                        Monday - Saturday
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <a
                href="https://wa.me/919999742835?text=Hello%20Dr%20Amit%2C%20I%20would%20like%20to%20book%20an%20appointment%20for%20physiotherapy%20treatment."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full bg-green-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-green-700 transition-all transform hover:scale-[1.02] whitespace-nowrap cursor-pointer shadow-lg"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-2xl"></i>
                </div>
                <span className="text-sm sm:text-base">Book Appointment on WhatsApp</span>
              </a>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 animate-slide-in-right">
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Book an Appointment</h3>
              
              {formStatus === 'success' && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <strong className="text-green-900 block mb-1">Appointment Request Sent!</strong>
                    <p className="text-green-800 text-sm">We'll contact you shortly to confirm your appointment.</p>
                  </div>
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <strong className="text-red-900 block mb-1">Submission Failed</strong>
                    <p className="text-red-800 text-sm">Please try again or call us directly.</p>
                  </div>
                </div>
              )}

              <form className="space-y-4 sm:space-y-6" data-readdy-form id="appointment-form" onSubmit={handleSubmit}>
                <input type="hidden" name="email" value="dramitsharma.62@gmail.com" />
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label htmlFor="patient_email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Email Address *
                  </label>
                  <input
                    type="email"
                    id="patient_email"
                    name="patient_email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label htmlFor="date" className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Tell us about your condition or any specific concerns (max 500 characters)"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full bg-teal-600 text-white px-6 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-[1.02] whitespace-nowrap cursor-pointer shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Submit Appointment Request'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-700 to-teal-800 text-white py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="sm:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/5dfa6018-d6ac-4521-84f9-81506e526463.png" 
                  alt="Dr Amit Physiotherapy Clinic" 
                  className="h-10 w-10 sm:h-12 sm:w-12 object-contain"
                />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold">Dr Amit Physiotherapy Clinic</h3>
                  <p className="text-teal-100 text-xs sm:text-sm">Expert Care, Faster Recovery</p>
                </div>
              </div>
              <p className="text-teal-100 mb-4 leading-relaxed text-sm sm:text-base">
                Providing professional physiotherapy services in Ghaziabad for over 20 years. Dr. Amit Sharma and his team are committed to helping you achieve optimal health and wellness.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-base sm:text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2">
                {['Home', 'About', 'Services', 'Contact'].map(link => (
                  <li key={link}>
                    <button
                      onClick={() => scrollToSection(link.toLowerCase())}
                      className="text-teal-100 hover:text-white transition-colors text-sm cursor-pointer"
                    >
                      {link}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-base sm:text-lg mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-teal-100">
                <li>A 62, Block A, Brij Vihar</li>
                <li>Ghaziabad, UP 201011</li>
                <li>
                  <a href="tel:09999742835" className="hover:text-white transition-colors cursor-pointer">
                    099997 42835
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 text-center text-teal-100 text-xs sm:text-sm">
            <p>&copy; {new Date().getFullYear()} Dr Amit Physiotherapy Clinic. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
